import { useEffect, useRef, useState } from 'react'
import config from '../../../config'
import type {
    AdjuntoDTO,
    ClienteDTO,
    CreateAdjuntoRequest,
    CreateCRMRequest,
    CRMDTO,
    CRMTextoDTO,
    CRMTextoTipo,
    PagaebleResult,
    SearchPolizaDTO,
    StorageFile,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import {
    errorMessage,
    formatMonthYear,
    formatSpokenDate,
    isMoral,
    NOTE_TYPES,
    noteMeta,
    personName,
    statusKey,
} from '../common/customer-common'
import type { BitacoraFilter } from '../common/customer-common'

export type CustomerDrawerTab = 'general' | 'polizas' | 'bitacora' | 'adjuntos'

export interface CustomerDrawerProps {
    clienteId: number | null
    onClose: () => void
    onEdit?: (clienteId: number) => void
    initialTab?: CustomerDrawerTab
}

export type LoadState = 'idle' | 'loading' | 'ready' | 'error'

export interface CustomerDrawerTabItem {
    id: CustomerDrawerTab
    label: string
    count?: number | null
}

const POLIZA_STATUS_ORDER: Record<string, number> = {
    vigente: 0,
    anticipada: 1,
    gracia: 2,
    desprotegido: 3,
    'ot pendiente': 4,
    'ot proceso': 5,
    faltadetallepoliza: 6,
    faltadetallerecibos: 6,
    vencida: 7,
    renovada: 8,
    terminada: 9,
    precancelada: 10,
    cancelada: 11,
    eliminada: 12,
}

const polizaStatusRank = (status?: string | null) => POLIZA_STATUS_ORDER[statusKey(status)] ?? 50

const sortPolizas = (items: SearchPolizaDTO[]) => [...items].sort((left, right) => {
    const byStatus = polizaStatusRank(left.status) - polizaStatusRank(right.status)
    if (byStatus !== 0) return byStatus
    return String(left.fechaVigenciaA || '').localeCompare(String(right.fechaVigenciaA || ''))
})

export const isBusy = (state: LoadState) => state === 'idle' || state === 'loading'

export const useCustomerDrawerModel = ({ clienteId, initialTab = 'general' }: Pick<CustomerDrawerProps, 'clienteId' | 'initialTab'>) => {
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const vigentesLoadedFor = useRef<number | null>(null)
    const polizasLoadedFor = useRef<number | null>(null)
    const bitacoraLoadedFor = useRef<number | null>(null)
    const adjuntosLoadedFor = useRef<number | null>(null)
    const [cliente, setCliente] = useState<ClienteDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [tab, setTab] = useState<CustomerDrawerTab>('general')
    const [toast, setToast] = useState<string | null>(null)
    const [polizas, setPolizas] = useState<SearchPolizaDTO[]>([])
    const [polizasTotal, setPolizasTotal] = useState<number | null>(null)
    const [polizasState, setPolizasState] = useState<LoadState>('idle')
    const [polizasError, setPolizasError] = useState<string | null>(null)
    const [vigentesCount, setVigentesCount] = useState<number | null>(null)
    const [proximaVigencia, setProximaVigencia] = useState<string | null>(null)
    const [vigentesState, setVigentesState] = useState<LoadState>('idle')
    const [vigentesError, setVigentesError] = useState<string | null>(null)
    const [bitacora, setBitacora] = useState<CRMTextoDTO[]>([])
    const [bitacoraState, setBitacoraState] = useState<LoadState>('idle')
    const [bitacoraError, setBitacoraError] = useState<string | null>(null)
    const [noteType, setNoteType] = useState<CRMTextoTipo>(NOTE_TYPES[0].id)
    const [note, setNote] = useState('')
    const [savingNote, setSavingNote] = useState(false)
    const [bitacoraFilter, setBitacoraFilter] = useState<BitacoraFilter>('todo')
    const [adjuntos, setAdjuntos] = useState<AdjuntoDTO[]>([])
    const [adjuntosState, setAdjuntosState] = useState<LoadState>('idle')
    const [adjuntosError, setAdjuntosError] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 3000)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        if (!clienteId) return undefined
        setTab(initialTab)
        setPolizas([])
        setPolizasTotal(null)
        setPolizasState('idle')
        setPolizasError(null)
        polizasLoadedFor.current = null
        setVigentesCount(null)
        setProximaVigencia(null)
        setVigentesState('idle')
        setVigentesError(null)
        vigentesLoadedFor.current = null
        setBitacora([])
        setBitacoraState('idle')
        setBitacoraError(null)
        bitacoraLoadedFor.current = null
        setNote('')
        setBitacoraFilter('todo')
        setAdjuntos([])
        setAdjuntosState('idle')
        setAdjuntosError(null)
        adjuntosLoadedFor.current = null
        setConfirmDeleteId(null)
        return undefined
    }, [clienteId, initialTab])

    useEffect(() => {
        if (!clienteId) return undefined
        let active = true
        const load = async () => {
            const fallback = 'No se pudo cargar el detalle del cliente.'
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/clientes/${clienteId}`)
                const payload: WrapperResponse<ClienteDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || fallback)
                setCliente(payload.body)
            } catch (loadError) {
                if (active) {
                    setCliente(null)
                    setError(errorMessage(loadError, fallback))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [clienteId, reloadToken])

    useEffect(() => {
        if (!clienteId || tab !== 'general' || vigentesLoadedFor.current === clienteId) return undefined
        let active = true
        const load = async () => {
            const fallback = 'No se pudieron cargar las pólizas vigentes.'
            try {
                setVigentesState('loading')
                setVigentesError(null)
                const response = await APIInvoke.invokeGET(`/polizas?clienteId=${clienteId}&status=vigente&size=1&order=fechaVigenciaA asc`)
                const payload: WrapperResponse<PagaebleResult<SearchPolizaDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || fallback)
                const items = payload.body?.items || []
                setVigentesCount(payload.body?.totalItems ?? items.length)
                setProximaVigencia(items[0]?.fechaVigenciaA || null)
                setVigentesState('ready')
                vigentesLoadedFor.current = clienteId
            } catch (loadError) {
                if (active) {
                    setVigentesState('error')
                    setVigentesError(errorMessage(loadError, fallback))
                }
            }
        }
        load()
        return () => { active = false }
    }, [clienteId, tab])

    useEffect(() => {
        if (!clienteId || tab !== 'polizas' || polizasLoadedFor.current === clienteId) return undefined
        let active = true
        const load = async () => {
            const fallback = 'No se pudieron cargar las pólizas.'
            try {
                setPolizasState('loading')
                setPolizasError(null)
                const response = await APIInvoke.invokeGET(`/polizas?clienteId=${clienteId}&size=100&order=fechaVigenciaA asc`)
                const payload: WrapperResponse<PagaebleResult<SearchPolizaDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || fallback)
                const items = payload.body?.items || []
                setPolizas(items)
                setPolizasTotal(payload.body?.totalItems ?? items.length)
                setPolizasState('ready')
                polizasLoadedFor.current = clienteId
            } catch (loadError) {
                if (active) {
                    setPolizasState('error')
                    setPolizasError(errorMessage(loadError, fallback))
                }
            }
        }
        load()
        return () => { active = false }
    }, [clienteId, tab])

    useEffect(() => {
        if (!clienteId || tab !== 'bitacora' || bitacoraLoadedFor.current === clienteId) return undefined
        let active = true
        const load = async () => {
            const fallback = 'No se pudo cargar la bitácora.'
            try {
                setBitacoraState('loading')
                setBitacoraError(null)
                const response = await APIInvoke.invokeGET(`/crm?clienteId=${clienteId}`)
                const payload: WrapperResponse<CRMDTO[]> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || fallback)
                const textos = payload.body?.[0]?.textos || []
                setBitacora([...textos].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || ''))))
                setBitacoraState('ready')
                bitacoraLoadedFor.current = clienteId
            } catch (loadError) {
                if (active) {
                    setBitacoraState('error')
                    setBitacoraError(errorMessage(loadError, fallback))
                }
            }
        }
        load()
        return () => { active = false }
    }, [clienteId, tab])

    useEffect(() => {
        if (!clienteId || tab !== 'adjuntos' || adjuntosLoadedFor.current === clienteId) return undefined
        let active = true
        const load = async () => {
            const fallback = 'No se pudieron cargar los adjuntos.'
            try {
                setAdjuntosState('loading')
                setAdjuntosError(null)
                const response = await APIInvoke.invokeGET(`/adjuntos/clientes/${clienteId}`)
                const payload: WrapperResponse<AdjuntoDTO[]> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || fallback)
                setAdjuntos(payload.body || [])
                setAdjuntosState('ready')
                adjuntosLoadedFor.current = clienteId
            } catch (loadError) {
                if (active) {
                    setAdjuntosState('error')
                    setAdjuntosError(errorMessage(loadError, fallback))
                }
            }
        }
        load()
        return () => { active = false }
    }, [clienteId, tab])

    const addNote = async () => {
        const texto = note.trim()
        if (!texto || !cliente) return
        try {
            setSavingNote(true)
            const request: CreateCRMRequest = {
                asunto: 'bitacora',
                tipo: noteType,
                clienteId: cliente.id,
                textos: [{ tipo: noteType, texto }],
            }
            const response = await APIInvoke.invokePOST('/crm', request)
            const payload: WrapperResponse<CRMDTO> | null = await response.json()
            if (!payload?.ok) throw new Error(payload?.message || 'No se pudo guardar la bitácora.')
            const created = payload.body?.textos?.[0]
            if (created) setBitacora((items) => [created, ...items])
            setNote('')
            setBitacoraFilter('todo')
            showToast('Registro agregado a la bitácora')
        } catch (saveError) {
            showToast(errorMessage(saveError, 'No se pudo guardar la bitácora.'))
        } finally {
            setSavingNote(false)
        }
    }

    const openFile = async (path?: string | null) => {
        try {
            const url: string | null | undefined = await APIInvoke.resolveStoredFileUrl(path)
            if (url) window.open(url, '_blank', 'noopener')
        } catch (fileError) {
            showToast(errorMessage(fileError, 'No se pudo abrir el archivo.'))
        }
    }

    const uploadFiles = async (fileList: FileList | null) => {
        const files = Array.from(fileList || [])
        if (!files.length || !cliente) return
        try {
            setUploading(true)
            const created: AdjuntoDTO[] = []
            for (const file of files) {
                const body = new FormData()
                body.append('bucket', config.bucket.name)
                body.append('path', `clientes/${file.name}`)
                body.append('file', file)
                const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
                if (!uploaded.ok || !uploaded.body) throw new Error(uploaded.message || 'No se pudo subir el archivo.')
                const request: CreateAdjuntoRequest = {
                    clienteId: cliente.id,
                    dominio: '',
                    path: uploaded.body.url,
                    archivo: file.name,
                }
                const saved: WrapperResponse<AdjuntoDTO> = await (await APIInvoke.invokePOST('/adjuntos', request)).json()
                if (!saved.ok || !saved.body) throw new Error(saved.message || 'No se pudo registrar el archivo.')
                created.push(saved.body)
            }
            setAdjuntos((items) => [...created, ...items])
            showToast(files.length === 1 ? 'Archivo subido' : `${files.length} archivos subidos`)
        } catch (uploadError) {
            showToast(errorMessage(uploadError, 'No se pudo subir el archivo.'))
        } finally {
            setUploading(false)
        }
    }

    const deleteAdjunto = async (id: number) => {
        try {
            const payload: WrapperResponse<unknown> | null = await (await APIInvoke.invokeDELETE(`/adjuntos/${id}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || 'No se pudo eliminar el archivo.')
            setAdjuntos((items) => items.filter((item) => item.id !== id))
            setConfirmDeleteId(null)
            showToast('Archivo eliminado')
        } catch (deleteError) {
            showToast(errorMessage(deleteError, 'No se pudo eliminar el archivo.'))
        }
    }

    const name = cliente ? personName(cliente) : ''
    const direccion = cliente?.direcciones?.[0]
    const bitacoraNotas = bitacora.filter((item) => statusKey(item.tipo) !== 'adjunto')
    const tabs: CustomerDrawerTabItem[] = [
        { id: 'general', label: 'Datos generales' },
        { id: 'polizas', label: 'Pólizas', count: polizasState === 'ready' ? polizasTotal : null },
        { id: 'bitacora', label: 'Bitácora', count: bitacoraState === 'ready' ? bitacoraNotas.length : null },
        { id: 'adjuntos', label: 'Adjuntos', count: adjuntosState === 'ready' ? adjuntos.length : null },
    ]

    return {
        cliente,
        loading,
        error,
        tab,
        toast,
        tabs,
        name,
        moral: isMoral(cliente?.fiscal),
        activo: statusKey(cliente?.status) === 'activo',
        direccion,
        sepomex: direccion?.sepomex,
        apellidos: [cliente?.apPat, cliente?.apMat].filter(Boolean).join(' '),
        altaSpoken: formatSpokenDate(cliente?.fecRegistro),
        clienteDesde: formatMonthYear(cliente?.fecRegistro),
        vigentesCount,
        proximaVigencia,
        vigentesState,
        vigentesError,
        polizas,
        polizasOrdenadas: sortPolizas(polizas),
        polizasState,
        polizasError,
        bitacoraState,
        bitacoraError,
        bitacoraFilter,
        visibleNotes: bitacoraNotas.filter((item) => bitacoraFilter === 'todo' || noteMeta(item.tipo).filter === bitacoraFilter),
        noteType,
        note,
        notePlaceholder: NOTE_TYPES.find((item) => item.id === noteType)?.placeholder,
        savingNote,
        adjuntos,
        adjuntosState,
        adjuntosError,
        uploading,
        confirmDeleteId,
        setTab,
        setNoteType,
        setNote,
        setBitacoraFilter,
        setConfirmDeleteId,
        reload: () => setReloadToken((token) => token + 1),
        addNote,
        openFile,
        uploadFiles,
        deleteAdjunto,
    }
}
